import type { Address, Hex } from "viem";
import type { BatchSize, PriceRule, ServiceKind } from "./pricing";

/** A spend shown in the Console: one per settle (D38). Amounts in whole tokens; credits in wpCredit base units. */
export interface Spend {
  id: string;
  time: string; // ISO 8601
  kind: ServiceKind;
  name: string; // the model, the tool, "round", "commit", "cf.deploy_worker"
  service: string; // owner@addr4/service
  token: string; // the symbol
  tokenAddress: Address;
  actual: number;
  rounds?: number;
  minted?: number; // the part paid in wpCredit and minted to payTo (D67)
  credits?: string; // wpCredit base units paid for the shortfall
  tx: string;
  state: "settled";
}

export type ConversationStatus = "idle" | "running" | "paused" | "stopped" | "done";

export interface PauseInfo {
  /** `pricing`: the service's price document changed since its owner committed its hash in the directory (item 11). */
  reason: "cap" | "balance" | "allowance" | "budget" | "nonce" | "rejected" | "pricing" | "unknown";
  token: string; // the symbol
  tokenAddress?: Address;
  service?: string;
  cap?: string;
  used?: string;
  needs?: string;
  holds?: string;
  detail?: string;
}

export type MessageRole = "user" | "assistant" | "tool" | "signature" | "summary" | "system";

export interface ChatMessage {
  role: MessageRole;
  text?: string;
  model?: string;
  spend?: string; // "2 tLLM"
  tool?: string;
  args?: string;
  result?: string;
  price?: string;
  lines?: string[];
  state?: "settled" | "pending" | "failed";
  failed?: boolean;
  service?: string;
  // a wallet card inside a deployment's transcript (D30)
  id?: string;
  title?: string;
  note?: string;
  signatures?: CardLine[];
  rows?: Array<{ label: string; value: string }>;
  confirmed?: boolean;
  rejected?: boolean;
  resultText?: string;
}

export interface CardLine {
  text: string; // the identifier line, e.g. "setService(submitter 0x…)"
  plain: string; // the plain words of the signing panel (D45)
  state: "pending" | "signing" | "signed";
  tx?: string;
  call?: boolean; // a line that is not a signature (a free check)
  kind?: CardTxKind;
  args?: Record<string, string>;
}

export type CardTxKind =
  | "approve"
  | "authorize"
  | "fund"
  | "deployToken"
  | "setService"
  | "setMintAllowance"
  | "claim"
  | "set"
  | "setPricingHash"
  | "mint"
  | "message";

export interface ConversationSummary {
  id: string;
  kind: "conversation" | "deploy";
  title: string;
  agent: Address;
  runner: string;
  memory: string;
  model: string; // "gateway|model"
  tools: string[];
  status: ConversationStatus;
  lastActivity: string;
  effort?: string;
  spent: Record<string, number>; // symbol → actual
  credits?: string;
}

export interface Turn {
  turn: number;
  state: "pending" | "confirmed";
  messages: ChatMessage[];
  spends: Spend[];
  committedAt: string;
}

export interface SettingsRecord {
  myServices: string[];
  defaultCaps: Record<string, number>; // symbol → cap
  defaultModel: string; // "gateway|model"
  defaultEffort: string;
  defaultRunner: string;
  defaultMemory: string;
  /** The install records of the wallet's extensions (D72, D74); absent on a record older than them. */
  extensions?: InstallRecord[];
}

/**
 * An installed extension as the settings record keeps it (D72, D74): what was installed, pinned by its hash, the
 * identity derived for it in the browser, the store chosen, and the caps the wallet set at install (the chain holds
 * the caps that count; these are what the card asked). The memory service binds the extension's configuration
 * namespace to `identity` (D51b).
 */
export interface InstallRecord {
  id: string; // handle/name
  owner: Address; // the publisher's wallet: the directory's entry is (owner, name)
  name: string; // the manifest's name, for the menu item
  icon?: string; // the manifest's icon name
  version: string;
  hash: Hex; // the manifest's sha256, pinned at install (D79)
  url: string; // the version's url, pinned: the chain's url moves with new versions
  identity: Address;
  store?: string; // a service of kind store, when the manifest declares storage.store
  storage: { config?: string; store?: string };
  services: string[];
  caps: Record<string, number>; // symbol → cap asked at install
  enabled: boolean;
  installedAt: string; // ISO 8601
}

export const EFFORTS = ["off", "minimal", "low", "medium", "high", "xhigh", "max"] as const;
export type Effort = (typeof EFFORTS)[number];

/** The form of the deploy page (D30, D35, D37, D39, D57, D66). */
export interface DeployForm {
  preset: string;
  id: string;
  symbol: string;
  labels: string[];
  icon?: string;
  pricing: PriceRule;
  rate: string; // wpCredit base units per 1 token
  batch: BatchSize;
  models?: string[];
  upstreamUrl?: string;
  /** The directory handle (owner@addr4/service), filled by the runner from the wallet. */
  handle?: string;
}

export interface Caps {
  [symbol: string]: number;
}

export type HexString = Hex;
